import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import { Loading } from "@pages/v2/components/layout/Loading";
import {
  formatAmount,
  formatNullableDate,
  LEGACY_BADGE_CLASS,
  MISSING_VALUE,
  statusBadgeClass,
} from "@pages/v2/queries/transactions/format";
import { fetchLegacyTransactionDetail } from "@pages/v2/queries/transactions/transactionQueries";
import type { LegacyTransactionRow } from "@pages/v2/queries/transactions/types";

// Read-only detail for an imported Broadleaf-era purchase (OMG-2574). Unlike
// TransactionDetail there is no timeline (the old site kept no per-charge log
// we imported) and no Void / Settle: the row is history, nothing about it is
// actionable. The charge is rendered once and each holder of the id gets a
// card: a gift whose recipient created their account at redemption has two
// (purchaser first); any other purchase, gift or not, has one.
const LegacyTransactionDetail = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();

  // Same react-query setup as the User page's transactions section: no retry,
  // a 404 (not a legacy id) is a final answer, not a flaky request.
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-legacy-transaction", transactionId] as const,
    queryFn: () => fetchLegacyTransactionDetail(transactionId as string),
    enabled: !!transactionId,
    retry: false,
  });

  const back = (
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="flex items-center gap-1 bg-transparent text-sm text-gray-600 hover:text-gray-900 focus:outline-none"
    >
      <ArrowLeft size={16} /> Back
    </button>
  );

  if (isLoading) return <Loading />;
  if (error) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <div className="rounded-md bg-red-50 p-4 text-red-700 text-sm">
          {error.message}
        </div>
      </div>
    );
  }
  // Unreachable today (an empty result 404s upstream and lands in the error
  // branch), but never strand the admin on a blank page without a way back.
  if (!data || data.rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {back}
        <p className="text-sm text-gray-500">
          No legacy transaction found for this id.
        </p>
      </div>
    );
  }

  // Charge-level fields are identical on every holder row (they come from
  // PAYMENT_AUTHORIZATION and the Braintree warehouse, keyed by id alone).
  const charge = data.rows[0];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">{back}</div>

      <section className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold mb-3">
          Transaction
          <span className={LEGACY_BADGE_CLASS}>legacy</span>
        </h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <DtDd label="ID" value={charge.transactionId} />
          <DtDd
            label="Status"
            value={
              <span className={statusBadgeClass(charge.status)}>
                {charge.status}
              </span>
            }
          />
          <DtDd
            label="Amount"
            value={formatAmount(charge.amount, charge.currency)}
          />
          <DtDd
            label="Authorized at"
            value={formatNullableDate(charge.authorizedAt, formatUtc)}
          />
          <DtDd
            label="Settled at"
            value={formatNullableDate(charge.settledAt, formatUtc)}
          />
        </dl>
        <p className="mt-3 text-xs text-gray-500">
          Imported from the legacy site. Amount is filled from the Braintree
          warehouse by a separate enrichment run; {MISSING_VALUE} means that run
          has not covered this row, or the id is a PayPal or Epoch one with no
          Braintree record. No timeline or actions are available.
        </p>
      </section>

      <section className="rounded-md border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold mb-3">
          {data.rows.length > 1 ? `Holders (${data.rows.length})` : "Holder"}
        </h3>
        {data.truncated ? (
          <p className="mb-3 text-xs text-red-700">
            More holders exist than shown. A real purchase has at most two; this
            id is a campaign label the legacy gift tooling wrote onto every
            recipient, not a transaction.
          </p>
        ) : data.rows.length > 1 ? (
          <p className="mb-3 text-xs text-gray-500">
            {data.rows.length} legacy accounts carry this id: a gift whose
            recipient created their account at redemption. Purchaser first.
          </p>
        ) : (
          <p className="mb-3 text-xs text-gray-500">
            One holder does not rule out a gift: a gift redeemed by an existing
            account was recorded on the purchaser only.
          </p>
        )}
        <div className="flex flex-col gap-3">
          {data.rows.map((row) => (
            <HolderCard
              key={`${row.transactionId}-${row.legacyCustomerId ?? row.email}`}
              row={row}
            />
          ))}
        </div>
      </section>
    </div>
  );
};

const HolderCard = ({ row }: { row: LegacyTransactionRow }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border border-gray-100 bg-gray-50 p-3 text-sm">
    <DtDd label="Email" value={row.email} />
    <DtDd
      label="Customer ID"
      value={
        row.customerId ? (
          <Link
            className="text-blue-600 hover:underline"
            to={`/users/${row.customerId}`}
          >
            {row.customerId}
          </Link>
        ) : (
          <span
            className="text-gray-500"
            title="This legacy account has not signed in on the new site yet, so it has no user record."
          >
            not linked
          </span>
        )
      }
    />
    <DtDd
      label="Legacy customer ID"
      value={row.legacyCustomerId ?? MISSING_VALUE}
    />
    {/* OMGYES_CUSTOMER.PURCHASE was the account's FINAL product string, not
        the product of this particular charge, hence the label. */}
    <DtDd
      label="Products (account total)"
      value={row.purchaseOption ?? MISSING_VALUE}
    />
    <DtDd
      label="Purchase country"
      value={row.purchaseCountry?.toUpperCase() ?? MISSING_VALUE}
    />
  </dl>
);

// Same UTC rendering as TransactionDetail (OMG-2324): the listing's day
// filter brackets UTC days, so the detail must show the same day.
const formatUtc = (date: Date): string =>
  `${date.toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  })} UTC`;

const DtDd = ({ label, value }: { label: string; value: ReactNode }) => (
  <>
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-gray-900">{value}</dd>
  </>
);

export default LegacyTransactionDetail;
